import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthLayout from "../components/layout/AuthLayout";
import Button from "../components/common/Button";
import StatusBadge from "../components/common/StatusBadge";
import { fetchCurrentAgent, logoutAgentAndRedirect } from "../lib/agentAuth";
import { getAgentHomePath } from "../lib/agentOnboarding";
import { BRAND } from "../lib/brand";
import { showApiError } from "../lib/alerts";
import { getAgentToken } from "../lib/tokenStore";

export default function WaitingApprovalPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const fromRegistration = Boolean(location.state?.fromRegistration);
  const [profile, setProfile] = useState(null);
  const [checking, setChecking] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = useCallback(async ({ silent = false } = {}) => {
    if (!getAgentToken()) {
      navigate("/login", { replace: true });
      return null;
    }

    if (!silent) {
      setRefreshing(true);
    }

    try {
      const current = await fetchCurrentAgent({ force: true });
      setProfile(current);
      const nextPath = getAgentHomePath(current);
      if (nextPath !== "/waiting-approval") {
        navigate(nextPath, { replace: true });
      }
      return current;
    } catch (error) {
      if (error?.status === 401) {
        navigate("/login", { replace: true });
        return null;
      }
      showApiError(error, "CHECK_APPROVAL");
      return null;
    } finally {
      setChecking(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadStatus({ silent: true });
  }, [loadStatus]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadStatus({ silent: true });
    }, 15000);
    return () => window.clearInterval(timer);
  }, [loadStatus]);

  const referenceId = profile?.id || profile?.utp_id || location.state?.partnerId;
  const email = profile?.email || location.state?.email || "";

  return (
    <AuthLayout
      heroImage="/template/assets/images/auth/agent_register.png"
      heroAlt="Waiting for Way2Umrah approval"
    >
      <div className="max-w-560-px mx-auto w-100 auth-form-card">
        <Link to="/waiting-approval" className="mb-32 max-w-290-px d-inline-block">
          <img src={BRAND.logo} alt={BRAND.name} className="auth-brand-logo" />
        </Link>
        {fromRegistration ? (
          <div className="alert alert-success bg-success-100 text-success-600 border-success-600 border-start-width-4-px border-top-0 border-end-0 border-bottom-0 px-24 py-13 mb-24 fw-semibold text-lg radius-4">
            <h5 className="mb-8">Registration Submitted Successfully</h5>
            <p className="mb-0">Your account is waiting for approval from Way2Umrah.</p>
          </div>
        ) : (
          <h4 className="mb-12">Waiting for Approval</h4>
        )}
        <p className="text-secondary-light mb-24">
          Keep this page open. When Way2Umrah activates your agency, you will continue to onboarding without signing in again.
        </p>
        <div className="card border shadow-none mb-24">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-16">
              <div className="text-uppercase text-secondary small fw-semibold">Status</div>
              <StatusBadge value="Pending Approval" />
            </div>
            <div className="d-flex justify-content-between gap-3 py-2 border-bottom">
              <span className="text-secondary">Application ID</span>
              <span className="fw-semibold">{referenceId ? `W2U-AG-${referenceId}` : "Assigned after submission"}</span>
            </div>
            <div className="d-flex justify-content-between gap-3 py-2 border-bottom">
              <span className="text-secondary">Account status</span>
              <span className="fw-semibold text-capitalize">{profile?.status || "pending"}</span>
            </div>
            <div className="d-flex justify-content-between gap-3 py-2">
              <span className="text-secondary">Email</span>
              <span className="fw-semibold">{email || "Not available"}</span>
            </div>
          </div>
        </div>
        <Button
          type="button"
          className="btn btn-primary-600 w-100 radius-12"
          disabled={checking || refreshing}
          onClick={() => loadStatus()}
        >
          {refreshing ? "Checking status..." : "Refresh status"}
        </Button>
        <button
          type="button"
          className="btn btn-link w-100 mt-12"
          onClick={() => logoutAgentAndRedirect()}
        >
          Sign out
        </button>
      </div>
    </AuthLayout>
  );
}
